import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Users, Upload, Trash2, Palette, FileSpreadsheet, Printer, BarChart3 } from "lucide-react";
import { Shell, Stat, Modal } from "../components/Shell";
import { ClassReport } from "../components/ClassReport";
import { useLang } from "../i18n";
import { api, API, useAuth } from "../api";
import { ASSETS } from "../assets";

const GRADES = [3, 4, 5, 6, 7, 8, 9];

export default function SchoolAdmin() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const [stats, setStats] = useState({});
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [target, setTarget] = useState(null);
  const [sel, setSel] = useState({ student_ids: [], teacher_ids: [] });
  const [school, setSchool] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [classReport, setClassReport] = useState(null);

  const openClassReport = async (cls) => {
    setTarget(cls); setClassReport(null); setModal("report");
    try {
      const { data } = await api.get(`/classes/${cls.id}/report`);
      setClassReport(data);
    } catch { toast.error("تعذّر تحميل التقرير"); }
  };

  const load = async () => {
    const [s, c, u, sc] = await Promise.all([api.get("/school/stats"), api.get("/classes"),
      api.get("/users"), api.get("/school/me")]);
    setStats(s.data); setClasses(c.data); setUsers(u.data); setSchool(sc.data);
  };
  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const nav = [{ key: "dashboard", to: "/school", label: t("dashboard"), icon: ASSETS.progress }];

  const saveBranding = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    setBusy(true);
    try {
      await api.put(`/schools/${school.id}`, {
        name: school.name, logo_color: f.get("logo_color"), accent_color: f.get("accent_color"),
      });
      const logo = f.get("logo");
      if (logo && logo.size) {
        const fd = new FormData(); fd.append("file", logo);
        await api.post(`/schools/${school.id}/logo`, fd);
      }
      toast.success("تم تحديث هوية المدرسة — حدّث الصفحة لرؤية الشعار");
      await load();
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "failed"); }
    finally { setBusy(false); }
  };

  const students = users.filter((u) => u.role === "student");
  const teachers = users.filter((u) => u.role === "teacher");

  const addClass = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    const { data } = await api.post("/classes", { name: f.get("name"), grade: Number(f.get("grade")) });
    setModal(null); toast.success("تم إنشاء الصف");
    await load();
    setTarget(data); setImportResult(null); setModal("students");
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/classes/template.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "students_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const importStudents = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("file", new FormData(e.target).get("file"));
    setBusy(true);
    try {
      const { data } = await api.post(`/classes/${target.id}/students/import`, fd);
      setImportResult(data);
      toast.success(`تم إنشاء ${data.created.length} طالب في ${data.class_name}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "تعذّر رفع الملف");
    } finally { setBusy(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post("/users", {
        username: f.get("username"), password: f.get("password"), full_name: f.get("full_name"),
        role: f.get("role"), grade: f.get("grade") ? Number(f.get("grade")) : null,
      });
      setModal(null); toast.success("تم إنشاء الحساب"); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "failed"); }
  };

  const importCsv = async (e) => {
    e.preventDefault();
    const fd = new FormData(); fd.append("file", new FormData(e.target).get("file"));
    try {
      const { data } = await api.post("/users/bulk-import", fd);
      toast.success(`تم إنشاء ${data.created} حساب`); setModal(null); load();
    } catch { toast.error("import failed"); }
  };

  const openMembers = (cls) => {
    setTarget(cls);
    setSel({ student_ids: cls.student_ids || [], teacher_ids: cls.teacher_ids || [] });
    setModal("members");
  };

  const toggle = (key, id) => setSel((s) => ({
    ...s, [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id],
  }));

  const saveMembers = async () => {
    await api.put(`/classes/${target.id}/members`, sel);
    setModal(null); toast.success("تم التوزيع"); load();
  };

  const resetPw = async (e) => {
    e.preventDefault();
    const pw = new FormData(e.target).get("password");
    await api.post(`/users/${target.id}/reset-password`, { password: pw });
    setModal(null); toast.success("تم تحديث كلمة السر");
  };

  return (
    <Shell nav={nav}>
      <h1 className="text-3xl sm:text-4xl font-black text-white glow-text">{t("school_admin")}</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        <Stat label={t("students")} value={stats.students ?? 0} testid="stat-students" />
        <Stat label={t("teachers")} value={stats.teachers ?? 0} testid="stat-teachers" />
        <Stat label={t("classes")} value={stats.classes ?? 0} testid="stat-classes" />
        <Stat label={t("quizzesTaken")} value={stats.quizzes_taken ?? 0} testid="stat-quizzes" />
        <Stat label={t("avgScore")} value={`${stats.avg_score ?? 0}%`} color="var(--play-secondary)" testid="stat-avg" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div className="pro-card lg:col-span-2" data-testid="branding-card">
          <h2 className="text-xl font-bold flex items-center gap-2"><Palette size={18} />{t("branding")}</h2>
          <form onSubmit={saveBranding} key={school?.id || "no-school"} className="flex flex-wrap gap-4 items-end mt-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#43537d]">{t("primaryColor")}</label>
              <input name="logo_color" type="color" className="input h-10 w-24 p-1" data-testid="brand-primary-input"
                defaultValue={school?.logo_color || "#635BFF"} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#43537d]">{t("accentColor")}</label>
              <input name="accent_color" type="color" className="input h-10 w-24 p-1" data-testid="brand-accent-input"
                defaultValue={school?.accent_color || "#FF9F1C"} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#43537d]">{t("logo")}</label>
              <input name="logo" type="file" accept="image/*" className="input" data-testid="brand-logo-input" />
            </div>
            {school?.logo_id && (
              <img src={`${API}/schools/${school.id}/logo?t=${school.logo_id}&auth=${localStorage.getItem("el_token")}`}
                alt="logo"
                className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid="brand-logo-preview" />
            )}
            <button disabled={busy} className="pro-btn text-white" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
              data-testid="brand-save-btn">{busy ? t("loading") : t("save")}</button>
          </form>
        </div>
        <div className="pro-card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{t("classes")}</h2>
            <button data-testid="add-class-btn" onClick={() => setModal("class")}
              className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
              <Plus size={16} />{t("addClass")}</button>
          </div>
          <table className="pro" data-testid="classes-table">
            <thead><tr><th>{t("name")}</th><th>{t("grade")}</th><th>{t("students")}</th><th>{t("teachers")}</th><th /><th /><th /><th /></tr></thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id} data-testid={`class-row-${c.id}`}>
                  <td className="font-bold">{c.name}</td><td>{c.grade}</td>
                  <td>{c.student_ids.length}</td><td>{c.teacher_ids.length}</td>
                  <td><button data-testid={`members-btn-${c.id}`} onClick={() => openMembers(c)}
                    className="pro-btn bg-white/12 text-white border border-white/20 flex items-center gap-1 text-xs">
                    <Users size={14} />{t("assign")}</button></td>
                  <td><button data-testid={`import-students-${c.id}`}
                    onClick={() => { setTarget(c); setImportResult(null); setModal("students"); }}
                    className="pro-btn bg-white/12 text-white border border-white/20 flex items-center gap-1 text-xs">
                    <FileSpreadsheet size={14} />رفع الطلاب</button></td>
                  <td><button data-testid={`class-report-${c.id}`}
                    onClick={() => openClassReport(c)}
                    className="pro-btn bg-white/12 text-white border border-white/20 flex items-center gap-1 text-xs">
                    <BarChart3 size={14} />تقرير</button></td>
                  <td><button data-testid={`delete-class-${c.id}`} className="text-red-500"
                    onClick={async () => { await api.delete(`/classes/${c.id}`); toast.success("deleted"); load(); }}>
                    <Trash2 size={15} /></button></td>
                </tr>
              ))}
              {!classes.length && <tr><td colSpan="8" className="text-white/45">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="pro-card">
          <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
            <h2 className="text-xl font-bold">{t("users")}</h2>
            <div className="flex gap-2">
              <button data-testid="import-csv-btn" onClick={() => setModal("import")}
                className="pro-btn border border-slate-200 flex items-center gap-1 text-sm"><Upload size={15} />CSV</button>
              <button data-testid="add-user-btn" onClick={() => setModal("user")}
                className="pro-btn text-white flex items-center gap-1" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
                <Plus size={16} />{t("addUser")}</button>
            </div>
          </div>
          <table className="pro" data-testid="school-users-table">
            <thead><tr><th>{t("fullName")}</th><th>{t("role")}</th><th>{t("grade")}</th><th /></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`}>
                  <td className="font-bold">{u.full_name}<div className="text-xs text-white/45">{u.username}</div></td>
                  <td>{t(u.role)}</td><td>{u.grade || "-"}</td>
                  <td><button data-testid={`reset-pw-${u.username}`}
                    onClick={() => { setTarget(u); setModal("pw"); }}
                    className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1"><KeyRound size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modal === "report"} onClose={() => setModal(null)}
        title={`تقرير الفصل — ${target?.name || ""}`} testid="class-report-modal">
        <ClassReport report={classReport} />
      </Modal>

      <Modal open={modal === "class"} onClose={() => setModal(null)} title={t("addClass")} testid="class-modal">
        <form onSubmit={addClass} className="space-y-3">
          <input name="name" className="input" placeholder="4-أ" required data-testid="class-name-input" />
          <select name="grade" className="input" data-testid="class-grade-select">
            {GRADES.map((g) => <option key={g} value={g}>{`${t("grade")} ${g}`}</option>)}
          </select>
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="class-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "members"} onClose={() => setModal(null)}
        title={`${t("assign")} — ${target?.name || ""}`} testid="members-modal">
        <div className="space-y-4">
          <div>
            <h4 className="font-bold mb-2">{t("students")}</h4>
            <div className="space-y-1 max-h-40 overflow-auto">
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`assign-student-${s.username}`}
                    checked={sel.student_ids.includes(s.id)} onChange={() => toggle("student_ids", s.id)} />
                  {s.full_name} <span className="text-white/45 text-xs">({t("grade")} {s.grade || "-"})</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-2">{t("teachers")}</h4>
            <div className="space-y-1 max-h-32 overflow-auto">
              {teachers.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`assign-teacher-${s.username}`}
                    checked={sel.teacher_ids.includes(s.id)} onChange={() => toggle("teacher_ids", s.id)} />
                  {s.full_name}
                </label>
              ))}
            </div>
          </div>
          <button onClick={saveMembers} data-testid="members-save-btn"
            className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>{t("save")}</button>
        </div>
      </Modal>

      <Modal open={modal === "students"} onClose={() => setModal(null)}
        title={`رفع قائمة الطلاب — ${target?.name || ""}`} testid="students-import-modal">
        <div className="space-y-4">
          <p className="text-sm text-white/70 leading-relaxed">
            ارفع ملف Excel بأعمدة: <b>الاسم · العائلة · اسم المستخدم · كلمة السر</b>.
            اترك كلمة السر فارغة وسيولّدها النظام تلقائياً.
            جميع الطلاب سيُضافون للصف <b>{target?.name}</b> (الصف الدراسي {target?.grade}).
          </p>
          <button onClick={downloadTemplate} data-testid="download-template-btn"
            className="pro-btn bg-white/12 text-white border border-white/20 text-sm flex items-center gap-2">
            <FileSpreadsheet size={15} />تحميل القالب الجاهز (xlsx)</button>
          <form onSubmit={importStudents} className="space-y-3">
            <input name="file" type="file" accept=".xlsx" className="input" required
              data-testid="students-file-input" />
            <button disabled={busy} data-testid="students-import-btn"
              className="pro-btn text-white w-full disabled:opacity-60"
              style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
              {busy ? "جارٍ الإنشاء..." : "إنشاء الحسابات"}</button>
          </form>

          {importResult && (
            <div className="border-t border-white/10 pt-4" data-testid="import-result">
              <div className="flex items-center justify-between">
                <h4 className="font-black text-sm">تم إنشاء {importResult.created.length} حساب</h4>
                <button onClick={() => window.print()} data-testid="print-accounts-btn"
                  className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
                  <Printer size={13} />طباعة</button>
              </div>
              <table className="pro mt-3">
                <thead><tr><th>الطالب</th><th>اسم المستخدم</th><th>كلمة السر</th></tr></thead>
                <tbody>
                  {importResult.created.map((s) => (
                    <tr key={s.username} data-testid={`created-${s.username}`}>
                      <td className="font-bold">{s.full_name}</td>
                      <td dir="ltr">{s.username}</td><td dir="ltr">{s.password}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!!importResult.errors.length && (
                <ul className="mt-3 text-xs text-red-300 space-y-1" data-testid="import-errors">
                  {importResult.errors.map((er, k) => <li key={k}>⚠️ {er}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </Modal>

      <Modal open={modal === "user"} onClose={() => setModal(null)} title={t("addUser")} testid="user-modal">
        <form onSubmit={addUser} className="space-y-3">
          <input name="full_name" className="input" placeholder={t("fullName")} required data-testid="user-name-input" />
          <input name="username" className="input" placeholder={t("username")} required data-testid="user-username-input" />
          <input name="password" className="input" placeholder={t("password")} required data-testid="user-password-input" />
          <select name="role" className="input" data-testid="user-role-select">
            <option value="student">{t("student")}</option>
            <option value="teacher">{t("teacher")}</option>
          </select>
          <select name="grade" className="input" data-testid="user-grade-select">
            <option value="">{t("grade")} —</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="user-save-btn">{t("save")}</button>
        </form>
      </Modal>

      <Modal open={modal === "import"} onClose={() => setModal(null)} title={t("bulkImport")} testid="import-modal">
        <form onSubmit={importCsv} className="space-y-3">
          <p className="text-sm text-white/70">CSV: username, password, full_name, role, grade</p>
          <input name="file" type="file" accept=".csv" className="input" required data-testid="import-file-input" />
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="import-submit-btn">{t("bulkImport")}</button>
        </form>
      </Modal>

      <Modal open={modal === "pw"} onClose={() => setModal(null)}
        title={`${t("resetPassword")} — ${target?.full_name || ""}`} testid="pw-modal">
        <form onSubmit={resetPw} className="space-y-3">
          <input name="password" className="input" placeholder={t("password")} required data-testid="new-password-input" />
          <button className="pro-btn text-white w-full" style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}
            data-testid="pw-save-btn">{t("save")}</button>
        </form>
      </Modal>
    </Shell>
  );
}
